import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import { ErrorBoundary } from '@sentry/react';
import { observer } from 'mobx-react';
import { ModelCreationType } from 'mobx-state-tree';
import React, { Suspense, useContext, useEffect, useState } from 'react';

import { AccessRevokedBanner } from 'src/accessRevoked/AccessRevokedBanner';
import { getEventToolTip } from 'src/calendars';
import EventCalendar from 'src/calendars/eventCalendar';
import { ClinicalSnapshot } from 'src/clinicalSnapshot';
import { Alert } from 'src/common/ui/components';
import { NAV_HEIGHT } from 'src/components/app/Nav/Nav';
import PatientPrescribe from 'src/components/pages/erx';
import SideVisit from 'src/components/pages/eventShow/SideVisit';
import { SideVisitContext } from 'src/components/pages/eventShow/SideVisitContext';
import PatientDebug from 'src/components/pages/pageElements/patientDebug/PatientDebug';
import PatientTasks from 'src/components/pages/pageElements/tasks/PatientTasks';
import TitleBar from 'src/components/pages/pageElements/titleBar';
import VisitNotes from 'src/components/pages/pageElements/visitNotes';
import PatientActivity from 'src/components/pages/patientActivity/PatientActivity';
import { PatientShowContext } from 'src/components/pages/patientShow/PatientShowContext';
import PatientDocuments from 'src/components/pages/patientShow/documents/patientDocuments';
import { ClinicActions } from 'src/dropInClinic/ClinicActions';
import PossibleDuplicateChartsBanner from 'src/duplicateCharts/PossibleDuplicateChartsBanner';
import { LastPatientVisitContext, MiniEvent } from 'src/events/LastPatientVisitContext';
import { getEventDisplayTitleWithoutPatient } from 'src/events/getEventDisplayTitle';
import { CLINICAL_SNAPSHOT } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import PocTesting from 'src/labs/PocTesting';
import Colors from 'src/nightingale/Colors';
import { ChartUpdateContextProvider } from 'src/nightingale/components/ChartUpdateContext/ChartUpdateContext';
import LazyLoadingSpinner from 'src/nightingale/components/common/LazyLoadingSpinner/LazyLoadingSpinner';
import { PageContainer } from 'src/nightingale/components/common/PageContainer/PageContainer';
import PatientOverview from 'src/nightingale/pages/PatientOverview';
import { PatientHeader } from 'src/patientHeader';
import PatientMenuV2 from 'src/patientMenu/PatientMenu';
import PatientPebbles from 'src/pebbles/pages/PatientPebbles';
import type { Provider } from 'src/shared/stores/resource';
import type { EventsStore } from 'src/stores/events';
import type { EventInstance, InstantiatedEvent } from 'src/stores/models/event';
import useRootStore from 'src/stores/useRootStore';
import { localZone } from 'src/util/timezones';

const ROLES_TO_REDIRECT_TO_SNAPSHOT = new Set(['clinician', 'registered_nurse']);

const PatientShow: React.FC = observer(() => {
  const rootStore = useRootStore();
  const {
    auth,
    events,
    patients,
    routerStore: {
      routerState: { params, queryParams },
    },
  } = rootStore;
  const classes = useStyles();
  const { calendar, patient } = patients;
  const { id, tab: tabParam, tabItem } = params;

  const [isLoading, setIsLoading] = useState(false);

  const flagClinicalSnapshot = useFeatureFlag(CLINICAL_SNAPSHOT);

  const teamRole = (auth.user as Provider)?.teamRole;
  const defaultTab =
    flagClinicalSnapshot && teamRole && ROLES_TO_REDIRECT_TO_SNAPSHOT.has(teamRole)
      ? 'clinicalSnapshot'
      : 'overview';
  const tab = tabParam ?? defaultTab;

  useEffect(() => {
    patients.reset();
  }, [id]);
  useEffect(() => {
    return () => patients.reset();
  }, []);

  useEffect(() => {
    const loadTab = async () => {
      await patients.loadPatientTab(id, tab, queryParams);
    };
    if (!isLoading) {
      setIsLoading(true);
      loadTab().then(() => {
        setIsLoading(false);
      });
    }
  }, [id, tab, queryParams]);

  const handleCreateEvent = (options?: Partial<ModelCreationType<EventInstance>>) => {
    events.createEvent(patient, options ? { start: options.start } : {});
  };

  const handleSaveEvent = (item, updates) => {
    events.updateEventAndSave(item, updates);
  };

  const { clearSideVisit, setSideVisit, sideVisits } = useContext(SideVisitContext);
  const sideVisitId = id ? sideVisits[id] : null;
  const { addLastPatientVisit } = useContext(LastPatientVisitContext);
  const { pageContentsRef } = useContext(PatientShowContext);

  useEffect(() => {
    loadSideVisit(sideVisitId, tab, events);
  }, [sideVisitId, tab]);

  useEffect(() => {
    updateSideVisit(tab, tabItem, id, events, setSideVisit);
  }, [id, tab, tabItem]);

  useEffect(() => {
    updateRecentVisitMenu(tab, id, events, addLastPatientVisit);
  }, [id, tab, events.event?.id]);

  useEffect(() => {
    pageContentsRef?.current?.scrollTo(0, 0);
  }, [tab]);

  if (!patient) return null;

  const timezone = patient.timezone ?? auth.user?.timezone ?? localZone;

  return (
    <div className={classes.root} data-testid="patient-show">
      <ErrorBoundary
        fallback={
          <Alert
            title="Error loading patient"
            message={`We were unable to load patient ${id} at this time.`}
            severity="error"
          />
        }
      >
        <div className={isVisitTab(tab) ? classes.noPrint : undefined}>
          <PatientMenuV2
            patientId={id}
            activeTab={tab}
            activeEventId={isVisitTab(tab) ? tabItem : undefined}
          />
        </div>
        <ChartUpdateContextProvider>
          <div className={classes.mainContent}>
            <PatientHeader patientId={id} activeTab={tab} />
            <div className={classes.columnContent}>
              <div className={classes.contents} ref={pageContentsRef}>
                <Suspense fallback={<LazyLoadingSpinner />}>
                  {tab !== 'erx' ? (
                    <PageContainer>
                      {tab === 'overview' && <PatientOverview patientId={id} />}
                      {tab === 'clinicalSnapshot' && flagClinicalSnapshot && (
                        <ClinicalSnapshot patientId={id} />
                      )}
                      {tab === 'activity' && <PatientActivity patientId={id} />}
                      {tab === 'calendar' && (
                        <EventCalendar
                          calendar={calendar}
                          calendarType="patient"
                          events={calendar.eventInstances}
                          onCreate={handleCreateEvent}
                          onSave={handleSaveEvent}
                          title="Patient Calendar"
                          titleBar={
                            <TitleBar
                              title="Patient Calendar"
                              onAction={() => handleCreateEvent()}
                              actionTooltip="Add event"
                              actionIcon={<AddIcon />}
                            />
                          }
                          titleAccessor={event =>
                            getEventDisplayTitleWithoutPatient(event, timezone)
                          }
                          tooltipAccessor={event =>
                            getEventToolTip(
                              event,
                              getEventDisplayTitleWithoutPatient(event, timezone),
                            )
                          }
                          showCheckbox
                          timezone={patient.timezone}
                          fallbackTimezone={timezone}
                        />
                      )}
                      {tab === 'documents' && <PatientDocuments patientId={id} />}
                      {tab === 'tasks' && <PatientTasks patientId={id} />}
                      {tab === 'clinicActions' && <ClinicActions patientId={id} />}
                      {tab === 'debug' && <PatientDebug data={patients.debug} patientId={id} />}
                      {isVisitTab(tab) && <VisitNotes />}
                      {tab === 'pebbles' && <PatientPebbles patientId={id} />}
                      {tab === 'pocTesting' && <PocTesting patientId={id} />}
                    </PageContainer>
                  ) : (
                    <PatientPrescribe patientId={id} />
                  )}
                </Suspense>
              </div>
              {!isVisitTab(tab) && sideVisitId ? (
                <SideVisit onClose={() => clearSideVisit(id)} />
              ) : null}
            </div>
          </div>
        </ChartUpdateContextProvider>
      </ErrorBoundary>
      {/* Alerts */}
      <ErrorBoundary
        fallback={
          <Alert
            title="Error loading patient banners"
            message={`We were unable to load patient banners for patient ${id} at this time.`}
            severity="error"
          />
        }
      >
        <AccessRevokedBanner open={!!patient.authenticationBlocked} />
        <PossibleDuplicateChartsBanner
          patientId={id}
          firstName={patient.firstName as string}
          lastName={patient.lastName as string}
          dob={patient.dob as string}
        />
      </ErrorBoundary>
    </div>
  );
});

function isVisitTab(tab: string): boolean {
  return tab === 'events';
}

function loadSideVisit(sideVisitId: string | null, tab: string, events: EventsStore): void {
  if (!sideVisitId) return;
  if (isVisitTab(tab)) return;

  events.loadEvent(sideVisitId);
}

function updateSideVisit(
  tab: string,
  tabItem: string,
  patientId: string | null | undefined,
  events: EventsStore,
  setSideVisit: (patientId: string, eventId: string) => void,
) {
  if (!isVisitTab(tab)) return;
  const eventId = tabItem;

  const { event: maybeEvent } = events;
  if (!maybeEvent || eventId !== maybeEvent?.id) return;
  const event = maybeEvent as InstantiatedEvent;

  if (patientId && eventId) {
    const patientAttendeeId = event.patientAttendee?.id;
    if (!patientAttendeeId || patientAttendeeId !== patientId) return;
    setSideVisit(patientId, eventId);
  }
}

function updateRecentVisitMenu(
  tab: string,
  patientId: string | null | undefined,
  events: EventsStore,
  addLastPatientVisit: (patientId: string, event: MiniEvent) => void,
) {
  if (!isVisitTab(tab)) return;

  const { event: maybeEvent } = events;
  if (!maybeEvent) return;
  const event = maybeEvent as InstantiatedEvent;

  if (patientId && event) {
    if (patientId === event.patientAttendee?.id && event.id) {
      addLastPatientVisit(patientId, {
        id: event.id,
        start: event.start,
        allDay: event.allDay,
        duration: event.duration,
      });
    }
  }
}

export const __test__ = {
  updateRecentVisitMenu,
  updateSideVisit,
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    width: '100%',
    minHeight: `calc(100vh - ${NAV_HEIGHT}px)`,
    backgroundColor: Colors.ChartGray,
  },
  contents: {
    flex: 1,
    overflow: 'scroll',
    '@media print': {
      height: 'auto',
      overflow: 'visible',
    },
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    maxHeight: `calc(100vh - ${NAV_HEIGHT}px)`,
    overflow: 'hidden',
    '@media print': {
      maxHeight: '100%',
    },
  },
  columnContent: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    overflow: 'hidden',
  },
  noPrint: {
    '@media print': {
      display: 'none',
    },
  },
  tabs: {
    marginLeft: 15,
  },
});

export default PatientShow;
